import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  ScheduleGroup,
  ScheduleGroupRequest,
  ScheduledItem,
  applyGroupRequest,
  formatGroupTime,
  groupByPublishMinute,
  toGroupRequest,
} from './scheduleGroups.domain';
import { useScheduleGroupActions } from './useScheduleGroupActions';

type GroupDrop = {
  workoutId: number;
  fromKey: number;
  toKey: number;
  index: number;
};

type GroupModal =
  | { type: 'reschedule'; group: ScheduleGroup }
  | { type: 'unschedule'; group: ScheduleGroup };

type UseScheduleGroupsParams = {
  programId: number;
  items: Array<ScheduledItem>;
  onChanged: () => void;
};

type PendingChange = {
  basis: string;
  apply: (items: Array<ScheduledItem>) => Array<ScheduledItem>;
};

const toBasis = (items: Array<ScheduledItem>) =>
  items.map((item) => `${item.id}@${item.publishedAt}`).join(',');

const workoutsLabel = (count: number) =>
  `${count} ${count === 1 ? 'workout' : 'workouts'}`;

const moveItem = (ids: Array<number>, id: number, index: number) => {
  const rest = ids.filter((v) => v !== id);
  return [...rest.slice(0, index), id, ...rest.slice(index)];
};

export const useScheduleGroups = ({
  programId,
  items,
  onChanged,
}: UseScheduleGroupsParams) => {
  const { state, scheduleGroup, unschedule } = useScheduleGroupActions(
    programId,
    onChanged,
  );
  const [modal, setModal] = useState<GroupModal | null>(null);
  const [pending, setPending] = useState<PendingChange | null>(null);

  // The change stays applied until the refetched list differs from the one it
  // was made against, so a drop or removal never snaps back while it saves.
  const basis = useMemo(() => toBasis(items), [items]);
  const shownItems = useMemo(
    () =>
      pending && pending.basis === basis && state.type !== 'fetch-error'
        ? pending.apply(items)
        : items,
    [items, basis, pending, state.type],
  );
  const groups = useMemo(() => groupByPublishMinute(shownItems), [shownItems]);

  const submit = useCallback(
    (request: ScheduleGroupRequest, successMessage?: string) => {
      setPending({
        basis,
        apply: (current: Array<ScheduledItem>) =>
          applyGroupRequest(current, request),
      });
      return scheduleGroup(request).then((ok) => {
        if (ok && successMessage) toast.success(successMessage);
      });
    },
    [basis, scheduleGroup],
  );

  const drop = useCallback(
    ({ workoutId, fromKey, toKey, index }: GroupDrop) => {
      const target = groups.find((group) => group.key === toKey);
      if (!target) return;
      if (fromKey !== toKey) {
        const title = items.find((item) => item.id === workoutId)?.title;
        return submit(
          toGroupRequest(groups, target.publishedAt, [workoutId], index),
          `${title} moved to ${formatGroupTime(target.publishedAt)}`,
        );
      }
      const ids = target.items.map((item) => item.id);
      const next = moveItem(ids, workoutId, index);
      if (next.every((id, i) => id === ids[i])) return;
      return submit(
        toGroupRequest(groups, target.publishedAt, next),
        'Order updated',
      );
    },
    [groups, items, submit],
  );

  const confirmReschedule = useCallback(
    (publishedAt: string) => {
      if (modal?.type !== 'reschedule') return;
      setModal(null);
      return submit(
        toGroupRequest(
          groups,
          publishedAt,
          modal.group.items.map((item) => item.id),
        ),
      );
    },
    [groups, modal, submit],
  );

  const confirmUnschedule = useCallback(() => {
    if (modal?.type !== 'unschedule') return;
    setModal(null);
    const count = modal.group.items.length;
    const ids = modal.group.items.map((item) => item.id);
    setPending({
      basis,
      apply: (current) => current.filter((item) => !ids.includes(item.id)),
    });
    return unschedule(ids).then((ok) => {
      if (ok)
        toast.success(
          `Group removed · ${workoutsLabel(count)} moved to Drafts`,
        );
    });
  }, [basis, modal, unschedule]);

  const openReschedule = useCallback(
    (group: ScheduleGroup) => setModal({ type: 'reschedule', group }),
    [],
  );
  const openUnschedule = useCallback(
    (group: ScheduleGroup) => setModal({ type: 'unschedule', group }),
    [],
  );
  const closeModal = useCallback(() => setModal(null), []);

  return {
    groups,
    state,
    modal,
    drop,
    confirmReschedule,
    confirmUnschedule,
    openReschedule,
    openUnschedule,
    closeModal,
  };
};
